import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChannelSelectionService } from '../../../services/channel-selection-service';
import {
  ConfirmationType,
  MassNotification,
  MassNotificationRecipient,
  MassNotificationRecipientResponseItem,
  MassNotificationRecipientResponsesRequest
} from '../../../store/communications';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { QueryParams, Workflow, WorkflowParentType, WorkflowStep, WorkflowStepTypes } from 'src/app/modules/core/store/models';
import { CommunicationsApiService } from '../../../store/communications/communications-api.service';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { Store } from '@ngrx/store';
import moment from 'moment';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { Subject } from 'rxjs';
import { TemplateDesignerService } from '../../../services/template-designer.service';

@Component({
    selector: 'topo-comms-notification-responses',
    templateUrl: './notification-responses.component.html',
    styleUrls: ['./notification-responses.component.scss'],
    standalone: false
})
export class NotificationResponsesComponent implements OnInit, OnDestroy {
  @Input() set notification(value: MassNotification) {
    if (this._notification?.uId !== value.uId) {
      this.selectedSortItem = this.sortItems[0];
      this._notification = value;
      this.responses = null;
      if (!!this._recipientWorkflow && !!value) {
        this.loadNotificationResponses(value);
      }
    }
  }
  _notification: MassNotification;
  get notification(): MassNotification {
    return this._notification;
  }

  _recipientWorkflow: Workflow;
  @Input() set recipientWorkflow(value: Workflow) {
    this._recipientWorkflow = value;
    if (!!this._notification && !!value) {
      this.loadNotificationResponses(this._notification);
    }
  }

  @Output() recipientClicked = new EventEmitter<MassNotificationRecipient>();

  totalRecords: number;
  responses: MassNotificationRecipientResponseItem[];

  destroy$: Subject<void> = new Subject<void>();

  sortItems = [
    { uId: '1', name: this.translateService.instant('COMMUNICATIONS.SORT-MOST-RECENT-FIRST'), icon: 'icon-small_arrow_sort_down' },
    { uId: '2', name: this.translateService.instant('COMMUNICATIONS.SORT-LEAST-RECENT-FIRST'), icon: 'icon-small_arrow_sort_up' }
  ];

  selectedSortItem: any;

  constructor(
    private store: Store<any>,
    private translateService: TranslateService,
    private commApiService: CommunicationsApiService,
    private channelSelectionService: ChannelSelectionService,
    private cultureService: CultureService,
    private templateDesignerService: TemplateDesignerService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.store
      .select(fromComm.selectIsdMassNotificationRecipientsTableChanged)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isChanged => {
        if (isChanged === true) {
          this.loadNotificationResponses(this._notification);
          this.changeDetector.markForCheck();
        }
      });

    // called when signalR updates the stats
    this.store
      .select(fromComm.selectSelectedMassNotificationStats)
      .pipe(takeUntil(this.destroy$))
      .subscribe(stats => {
        if (!!stats && this._notification?.uId === stats?.notification.uId) {
          if (!!stats?.notification) {
            this._notification = stats.notification;
            this.loadNotificationResponses(this._notification);
            this.changeDetector.markForCheck();
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadNotificationResponses(notification: MassNotification): void {
    if (!!!this._recipientWorkflow || !!!notification) {
      this.responses = [];
      return;
    }

    const escalatedStep = this._recipientWorkflow?.workflowSteps?.find(s => s.workflowStepType == WorkflowStepTypes.Escalated);

    const request: MassNotificationRecipientResponsesRequest = {
      massNotificationUId: notification.uId,
      timeOrderUp: this.selectedSortItem?.uId !== '1',
      workflowStepUIdsFilter: !!escalatedStep ? [escalatedStep.uId] : null
    };

    this.commApiService.getMassNotificationResponses(request).subscribe(res => {
      this.totalRecords = res.totalRecords;
      this.responses = res.responses;
      this.changeDetector.markForCheck();
      this.store.dispatch(fromComm.setMassNotificationRecipientsTableChanged({ changed: false }));
    });
  }

  getChannelIcon(channelUId: string): string {
    return this.channelSelectionService.getChannelIcon(channelUId);
  }

  getResponseTime(time: string): string {
    return this.cultureService.getDateDisplay(time, 'LONG-DATE-WITH-TIME');
  }

  getResponseTimePassed(time: string): string {
    return moment(time).from(new Date());
  }

  onSelectedSortChanged(item: any): void {
    this.selectedSortItem = this.sortItems.find(s => s.uId === item.uId);
    this.loadNotificationResponses(this.notification);
  }

  onRecipientClick(response: MassNotificationRecipientResponseItem): void {
    this.commApiService.getMassNotificationRecipient(response.massNotificationRecipientUId).subscribe(recipient => {
      this.recipientClicked.emit(recipient);
    });
  }

  public getConfirmationColor(response: MassNotificationRecipientResponseItem): string {
    if (!!response?.confirmationOptionsUId) {
      const confirmation = this.notification?.messageSettings?.confirmationButtons?.find(b => b.uId === response?.confirmationOptionsUId);
      if (!!confirmation) {
        return confirmation.type === ConfirmationType.Positive ? '#74B816' : '#E03131';
      }
    }
    return null;
  }

  public getConfirmationText(response: MassNotificationRecipientResponseItem): string {
    if (!!response?.confirmationOptionsUId) {
      const confirmation = this.notification?.messageSettings?.confirmationButtons?.find(b => b.uId === response?.confirmationOptionsUId);
      if (!!confirmation) {
        return this.templateDesignerService.getTranslateText(confirmation.text, this.cultureService.currentLang);
      }
    }
    return null;
  }
}
